import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, Popover, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { DndContext } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSortableSensors } from '../SortableTable';
import { CARD_TYPE_LIST, CardGroup, cardMeta, cardSummary } from './cardTypes';
import { CardType } from './validation';
import { Card } from './types';

interface StepRowProps {
  id: string;
  index: number;
  card: Card;
  selected: boolean;
  issueCount: number;
  onSelect: () => void;
}

const StepRow: React.FC<StepRowProps> = ({ id, index, card, selected, issueCount, onSelect }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const rowRef = useRef<HTMLDivElement | null>(null);
  const meta = cardMeta(card.type);
  const summary = cardSummary(card);

  useEffect(() => {
    if (selected) rowRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selected]);

  const classes = ['lbe-step', selected ? 'is-selected' : '', isDragging ? 'is-dragging' : ''].filter(Boolean).join(' ');

  return (
    <div
      ref={(el) => {
        setNodeRef(el);
        rowRef.current = el;
      }}
      className={classes}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      onClick={onSelect}
      {...attributes}
      {...listeners}
    >
      <span className="lbe-step-num">{index + 1}</span>
      <div className="lbe-step-main">
        <div className={`lbe-step-title${summary ? '' : ' is-empty'}`}>{summary || 'Untitled'}</div>
        <div className={`lbe-step-type group-${meta?.group ?? 'step'}`}>{meta?.label ?? card.type}</div>
      </div>
      {issueCount > 0 && (
        <Tooltip title={`${issueCount} thing${issueCount === 1 ? '' : 's'} to check`}>
          <Badge count={issueCount} size="small" color="#faad14" />
        </Tooltip>
      )}
    </div>
  );
};

interface StepListProps {
  cards: Card[];
  ids: string[];
  selectedIndex: number;
  issueCounts: number[];
  onSelect: (index: number) => void;
  onMove: (from: number, to: number) => void;
}

/** The sidebar's step list. Click to open a step; drag to reorder. */
export const StepList: React.FC<StepListProps> = ({ cards, ids, selectedIndex, issueCounts, onSelect, onMove }) => {
  const sensors = useSortableSensors();

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    onMove(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
  };

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {cards.map((card, i) => (
          <StepRow
            key={ids[i]}
            id={ids[i]}
            index={i}
            card={card}
            selected={i === selectedIndex}
            issueCount={issueCounts[i] ?? 0}
            onSelect={() => onSelect(i)}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

const GROUPS: { group: CardGroup; title: string; columns: number }[] = [
  { group: 'step', title: 'Steps', columns: 2 },
  { group: 'test', title: 'Tests', columns: 1 },
];

/** "Add step" button with a picker of every card type, grouped like the apps group them. */
export const AddStepButton: React.FC<{ onAdd: (type: CardType) => void; hint?: string; block?: boolean }> = ({
  onAdd, hint, block,
}) => {
  const [open, setOpen] = useState(false);

  const picker = (
    <div className="lbe-type-picker">
      {GROUPS.map(({ group, title, columns }) => (
        <div key={group}>
          <div className="lbe-type-picker-group">{title}</div>
          <div className="lbe-type-picker-list" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
            {CARD_TYPE_LIST.filter((meta) => meta.group === group).map((meta) => (
              <button
                key={meta.type}
                type="button"
                className="lbe-type-option"
                title={meta.description}
                onClick={() => {
                  setOpen(false);
                  onAdd(meta.type);
                }}
              >
                <div>{meta.label}</div>
                <div className="lbe-type-option-desc">{meta.description}</div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen} trigger="click" placement="rightBottom" title={hint} content={picker}>
      <Button block={block} type="dashed" icon={<PlusOutlined />}>Add step</Button>
    </Popover>
  );
};
